// Client for the user's agent API keys.  Plain authed fetches against
// /api/api-keys (JWT, the human in the app), distinct from the keys themselves
// which authenticate /api/agent.

export type ApiKeyScope = {
  allGroups: boolean;
  group: { id: string; name: string | null } | null;
};

export type ApiKey = {
  id: string;
  name: string;
  tokenPrefix: string;
  scope: ApiKeyScope;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revoked: boolean;
};

export type CreateKeyInput = {
  name: string;
  scopeGroupId: string | null;
  expiresInDays?: number;
};

function authHeaders(accessToken: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
}

async function parseError(r: Response): Promise<string> {
  const data = (await r.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? `HTTP ${r.status}`;
}

export async function listApiKeys(accessToken: string): Promise<ApiKey[]> {
  const r = await fetch("/api/api-keys", { headers: authHeaders(accessToken) });
  if (!r.ok) throw new Error(await parseError(r));
  const data = (await r.json()) as { keys: ApiKey[] };
  return data.keys;
}

export async function listKeyGroups(
  accessToken: string,
): Promise<Array<{ id: string; name: string }>> {
  const r = await fetch("/api/api-keys/groups", {
    headers: authHeaders(accessToken),
  });
  if (!r.ok) throw new Error(await parseError(r));
  const data = (await r.json()) as { groups: Array<{ id: string; name: string }> };
  return data.groups;
}

// Returns the created key metadata plus the raw secret, which is shown once.
export async function createApiKey(
  accessToken: string,
  input: CreateKeyInput,
): Promise<{ key: ApiKey; token: string }> {
  const r = await fetch("/api/api-keys", {
    method: "POST",
    headers: authHeaders(accessToken),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(await parseError(r));
  return (await r.json()) as { key: ApiKey; token: string };
}

export async function revokeApiKey(
  accessToken: string,
  id: string,
): Promise<void> {
  const r = await fetch(`/api/api-keys/${id}`, {
    method: "DELETE",
    headers: authHeaders(accessToken),
  });
  if (!r.ok) throw new Error(await parseError(r));
}
